import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { getStatusSummary } from "@/mock/data";
import { useData } from "@/context/DataContext";
import { Sheet, Input } from "@/pages/Familia";
import { scoreVehiculo, extractVehiculoData } from "@/lib/vehiculoMatch";
import { formatApiErrorDetail } from "@/lib/api";

const MiniCounter = ({ color, value, label }) => (
  <div className="flex-1 text-center">
    <p className="font-heading text-xl font-extrabold" style={{ color }}>{value}</p>
    <p className="text-xs font-semibold text-[#6E7A74]">{label}</p>
  </div>
);

const EMPTY = { alias: "", matricula: "", marca: "", modelo: "", anio: "", vin: "", titular: "", notas: "", icon: "Car" };

const VehiculoForm = ({ value, onChange, onSave, busy, title, saveLabel }) => (
  <>
    <h2 className="font-heading text-2xl font-bold text-[#1C2421] mb-5">{title}</h2>
    <Input label="Alias" value={value.alias} onChange={(v) => onChange({ ...value, alias: v })} testid="vehiculo-alias" placeholder="Ej. Coche Kia" />
    <Input label="Matrícula" value={value.matricula} onChange={(v) => onChange({ ...value, matricula: v })} testid="vehiculo-matricula" placeholder="Ej. 1234 ABC" />
    <Input label="Marca" value={value.marca} onChange={(v) => onChange({ ...value, marca: v })} testid="vehiculo-marca" placeholder="Ej. Kia" />
    <Input label="Modelo" value={value.modelo} onChange={(v) => onChange({ ...value, modelo: v })} testid="vehiculo-modelo" placeholder="Ej. Sportage" />
    <Input label="Año" value={value.anio} onChange={(v) => onChange({ ...value, anio: v })} testid="vehiculo-anio" placeholder="Opcional" />
    <Input label="VIN / bastidor" value={value.vin} onChange={(v) => onChange({ ...value, vin: v })} testid="vehiculo-vin" placeholder="Opcional" />
    <Input label="Titular" value={value.titular} onChange={(v) => onChange({ ...value, titular: v })} testid="vehiculo-titular" placeholder="Opcional" />
    <Input label="Notas" value={value.notas} onChange={(v) => onChange({ ...value, notas: v })} testid="vehiculo-notas" placeholder="Opcional" />
    <button data-testid="vehiculo-save" onClick={onSave} disabled={busy} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] disabled:opacity-60">{busy ? "Guardando…" : saveLabel}</button>
  </>
);

const PendientesVehiculo = () => {
  const navigate = useNavigate();
  const { matters, documents, vehiculos, asignarAsuntoAVehiculo, createVehiculo } = useData();
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [createForm, setCreateForm] = useState(null);

  const pendientes = useMemo(() => matters.filter((m) => m.category === "Vehículos" && !m.vehiculo_id), [matters]);
  const primaryDoc = (m) => documents.find((d) => (m.documents || []).includes(d.id)) || null;
  const suggestionFor = (m) => {
    const doc = primaryDoc(m);
    if (!doc || !doc.analisis || vehiculos.length === 0) return null;
    const data = extractVehiculoData(doc.analisis);
    const cands = vehiculos.map((v) => ({ veh: v, ...scoreVehiculo(data, v) })).filter((c) => c.reasons.length > 0 && c.confidence).sort((a, b) => b.score - a.score);
    return cands[0] || null;
  };
  const assign = async (m, vid) => {
    setBusyId(m.id);
    try { await asignarAsuntoAVehiculo(m.id, vid); toast.success("Asignado al vehículo"); setExpanded(null); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo asignar."); }
    finally { setBusyId(null); }
  };
  const openCreate = (m) => {
    const a = (primaryDoc(m)?.analisis?.vehiculo) || {};
    const gv = (k) => (a[k] && a[k].valor) || "";
    setCreateForm({ matter: m, ...EMPTY, alias: gv("marca") && gv("modelo") ? `${gv("marca")} ${gv("modelo")}` : (gv("matricula") || ""), matricula: gv("matricula"), marca: gv("marca"), modelo: gv("modelo"), vin: gv("vin"), titular: gv("titular") });
  };
  const createAndAssign = async () => {
    if (!createForm.alias.trim()) return toast.error("Indica un alias para el vehículo.");
    const matter = createForm.matter;
    setBusyId(matter.id);
    try {
      const { matter: _m, ...payload } = createForm;
      const nuevo = await createVehiculo({ ...payload, alias: payload.alias.trim() });
      await asignarAsuntoAVehiculo(matter.id, nuevo.id);
      toast.success("Vehículo creado y asunto asignado");
      setCreateForm(null); setExpanded(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo crear el vehículo."); }
    finally { setBusyId(null); }
  };

  if (pendientes.length === 0) return null;
  return (
    <section data-testid="vehiculo-pendientes" className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-[#1C2421]"><Icons.Inbox size={20} className="text-[#D97706]" /> Pendientes de asignar</h2>
        <span data-testid="vehiculo-pendientes-count" className="text-sm font-bold text-white bg-[#D97706] rounded-full px-2.5 py-0.5">{pendientes.length}</span>
      </div>
      <p className="text-sm text-[#6E7A74] -mt-1">Documentos de vehículo que aún no están asignados a uno concreto.</p>
      {pendientes.map((m) => {
        const doc = primaryDoc(m); const sug = suggestionFor(m); const isOpen = expanded === m.id;
        return (
          <div key={m.id} data-testid={`vehiculo-pendiente-${m.id}`} className="bg-white border border-[#E5DFD5] rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FEF3C7] flex items-center justify-center shrink-0"><Icons.FileText size={20} className="text-[#B45309]" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-base text-[#1C2421] truncate">{m.title}</p>
                <p className="text-sm text-[#6E7A74] truncate">{[m.provider, doc?.filename].filter(Boolean).join(" · ") || "Vehículos"}</p>
                {sug && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E5645] bg-[#E8F0EC] rounded-full px-2.5 py-1"><Icons.Sparkles size={13} /> Sugerencia: {sug.veh.alias} ({sug.reasons.join(", ")})</span>
                    <button data-testid={`vehiculo-pendiente-sug-${m.id}`} disabled={busyId === m.id} onClick={() => assign(m, sug.veh.id)} className="text-xs font-bold text-white bg-[#3A6B56] rounded-full px-3 py-1 hover:bg-[#2E5645] disabled:opacity-60">Asignar a {sug.veh.alias}</button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button data-testid={`vehiculo-pendiente-asignar-${m.id}`} onClick={() => setExpanded(isOpen ? null : m.id)} className="flex-1 min-w-[140px] min-h-[44px] rounded-xl bg-[#E8F0EC] text-[#2E5645] font-semibold text-sm border border-[#CDE3D6] hover:bg-[#DCEAE1] flex items-center justify-center gap-1.5"><Icons.Car size={16} /> Asignar a vehículo</button>
              <button data-testid={`vehiculo-pendiente-crear-${m.id}`} onClick={() => openCreate(m)} className="flex-1 min-w-[140px] min-h-[44px] rounded-xl bg-[#F2EDE4] text-[#4A5550] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#E5DFD5] flex items-center justify-center gap-1.5"><Icons.Plus size={16} /> Crear vehículo</button>
              <button data-testid={`vehiculo-pendiente-ver-${m.id}`} onClick={() => navigate(`/asunto/${m.id}`)} className="min-h-[44px] px-3 rounded-xl bg-white text-[#3A6B56] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#F6F3EE] flex items-center justify-center gap-1.5"><Icons.Eye size={16} /> Ver</button>
            </div>
            {isOpen && (
              <div data-testid={`vehiculo-pendiente-picker-${m.id}`} className="mt-3 border-t border-[#E5DFD5] pt-3 space-y-2">
                {vehiculos.length === 0 ? <p className="text-sm text-[#6E7A74]">Aún no tienes vehículos. Usa “Crear vehículo”.</p> : vehiculos.map((v) => (
                  <button key={v.id} data-testid={`vehiculo-pendiente-pick-${m.id}-${v.id}`} disabled={busyId === m.id} onClick={() => assign(m, v.id)} className="w-full text-left min-h-[48px] rounded-xl border border-[#E5DFD5] px-4 py-2 flex items-center justify-between hover:border-[#3A6B56] disabled:opacity-60">
                    <span className="min-w-0"><span className="font-semibold text-[#1C2421]">{v.alias}</span>{v.matricula ? <span className="text-sm text-[#6E7A74]"> · {v.matricula}</span> : null}</span>
                    <Icons.ChevronRight size={18} className="text-[#C4BDB0] shrink-0" />
                  </button>
                ))}
                <button data-testid={`vehiculo-pendiente-dejar-${m.id}`} onClick={() => setExpanded(null)} className="text-sm font-semibold text-[#6E7A74] min-h-[40px]">Dejar pendiente</button>
              </div>
            )}
          </div>
        );
      })}
      {createForm && (
        <Sheet onClose={() => setCreateForm(null)} testid="vehiculo-pendiente-crear-form">
          <VehiculoForm value={createForm} onChange={setCreateForm} onSave={createAndAssign} busy={busyId === createForm.matter.id} title="Crear vehículo y asignar" saveLabel="Crear y asignar" />
        </Sheet>
      )}
    </section>
  );
};

export default function Vehiculos() {
  const navigate = useNavigate();
  const { vehiculos, getMattersByVehiculo, createVehiculo } = useData();
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!modal.alias.trim()) return toast.error("Indica un alias para el vehículo.");
    setBusy(true);
    try { await createVehiculo({ ...modal, alias: modal.alias.trim() }); toast.success("Vehículo añadido"); setModal(null); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <button data-testid="vehiculos-back" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold text-base min-h-[44px]"><Icons.ChevronLeft size={22} /> Volver</button>
      <header className="aldia-fade-up flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center"><Icons.Car size={26} className="text-[#3A6B56]" /></div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">Vehículos</h1>
          <p className="text-base text-[#6E7A74]">Tus coches y motos: documentación, mantenimiento, seguros y más.</p>
        </div>
      </header>

      <button data-testid="vehiculo-add" onClick={() => setModal({ ...EMPTY })} className="w-full min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] transition-colors flex items-center justify-center gap-2"><Icons.Plus size={24} /> Añadir vehículo</button>

      {vehiculos.length === 0 ? (
        <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 text-center" data-testid="vehiculos-empty">
          <Icons.Car size={28} className="text-[#9AA39D] mx-auto mb-2" />
          <p className="text-base text-[#6E7A74]">Aún no has añadido ningún vehículo. Crea el primero para agrupar su documentación, mantenimiento y seguros.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="vehiculos-list">
          {vehiculos.map((veh, idx) => {
            const s = getStatusSummary(getMattersByVehiculo(veh.id));
            return (
              <button key={veh.id} data-testid={`vehiculo-card-${veh.id}`} onClick={() => navigate(`/vehiculo/${veh.id}`)} className="aldia-fade-up w-full text-left bg-white border border-[#E5DFD5] rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-[#3A6B56]/50 transition-all" style={{ animationDelay: `${idx * 70}ms` }}>
                <div className="flex items-center gap-4">
                  <div className="shrink-0 w-14 h-14 rounded-2xl bg-[#E8F0EC] flex items-center justify-center"><Icons.Car size={30} className="text-[#3A6B56]" /></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-xl font-bold text-[#1C2421]">{veh.alias}</h3>
                    <p className="text-sm text-[#6E7A74] truncate">{[veh.marca, veh.modelo].filter(Boolean).join(" ") || "Sin marca/modelo"}</p>
                    {(veh.matricula || veh.anio) && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E5645] bg-[#E8F0EC] rounded-full px-2.5 py-1 mt-1.5"><Icons.Hash size={13} /> {[veh.matricula, veh.anio].filter(Boolean).join(" · ")}</span>
                    )}
                  </div>
                  <Icons.ChevronRight size={22} className="text-[#C4BDB0] shrink-0" />
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#E5DFD5]/70">
                  <MiniCounter color="#DC2626" value={s.attention} label="Requiere atención" />
                  <div className="w-px h-8 bg-[#E5DFD5]" />
                  <MiniCounter color="#D97706" value={s.upcoming} label="Próximamente" />
                  <div className="w-px h-8 bg-[#E5DFD5]" />
                  <MiniCounter color="#16A34A" value={s.controlled} label="Documentación al día" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <PendientesVehiculo />

      <div className="flex items-start gap-2 bg-[#F6F3EE] rounded-xl p-4">
        <Icons.Info size={18} className="text-[#3A6B56] shrink-0 mt-0.5" />
        <p className="text-sm text-[#6E7A74] leading-relaxed">Permiso de circulación, ITV, impuestos, seguro y facturas de taller se agrupan en el vehículo al que pertenecen. Al subir un documento con matrícula o bastidor, AL DÍA propondrá vincularlo al vehículo correcto.</p>
      </div>

      {modal && (
        <Sheet onClose={() => setModal(null)} testid="vehiculo-form">
          <VehiculoForm value={modal} onChange={setModal} onSave={save} busy={busy} title="Añadir vehículo" saveLabel="Guardar vehículo" />
        </Sheet>
      )}
    </div>
  );
}
